import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Label,
    Select,
    SelectOption,
    Input,
    ControlError,
    Checkbox,
    Button,
    Toggle
} from "@forge/common";
import { CREATE_COURSE_CONSTANTS } from "../utils/constants";
import { NicConfigurationProps } from "../model/index";
import { getNicIpModes, getNicNetwork, getNicNetworkAdapterType } from "../store/create-course.action"
import { INetworkList, INetworkAdapter, INicIPModes } from "../model/index";
import { AppState } from "../../../../../common/store/combinedReducer";
import "./nic-configuration.scss";
import "../../create-course/create-course.scss";

const NicConfiguration: React.FC<NicConfigurationProps> = ({
    nicFormData,
    nicError,
    isEditMode,
    handleNicFormChange,
    handleAddNicToTable,
    validateIpAddress
}) => {
    const dispatch = useDispatch();
    const [networkList, setNetworkList] = React.useState<INetworkList[]>([]);
    const [networkAdapterList, setNetworkAdapterList] = React.useState<INetworkAdapter[]>([]);
    const [ipModes, setIpModes] = React.useState<INicIPModes[]>([]);

    const nicNetwork = useSelector((state: AppState) => {
        return state.createCourseReducer.networkList;
    });
    const nicNetworkAdapterType = useSelector((state: AppState) => {
        return state.createCourseReducer.nicNetworkAdapterTypes;
    });
    const nicIpModes = useSelector((state: AppState) => {
        return state.createCourseReducer.nicIpModes;
    });
    const currentCourseName = useSelector((state: AppState) => {
        return state.createCourseReducer.courseDefinitionData.courseDefData?.courseName;
    });
    useEffect(() => {
        if (!isEditMode) {
            dispatch(getNicNetwork());
            dispatch(getNicNetworkAdapterType());
            dispatch(getNicIpModes());
        }
    }, [])
    useEffect(() => {
        if (Array.isArray(nicNetwork) && nicNetwork.length > 0) {
            if (isEditMode && currentCourseName) {
                const filteredNetworks = nicNetwork.filter(network => {
                    if (network.networkValue.toLowerCase() === 'none') {
                        return true;
                    }
                    const networkWithoutPrefix = network.networkValue.replace(/^Int-/, '');
                    return networkWithoutPrefix.includes(currentCourseName);
                });
                // Include networks that contain the current course name
                setNetworkList(filteredNetworks);
            } else {
                // For create mode, show all networks
                setNetworkList(nicNetwork);
            }
        } else if (Array.isArray(nicNetwork) && nicNetwork.length === 0) {
            setNetworkList([]);
        }
    }, [nicNetwork, isEditMode, currentCourseName]);

    useEffect(() => {
        if (Array.isArray(nicNetworkAdapterType) && nicNetworkAdapterType.length > 0) {
            setNetworkAdapterList(nicNetworkAdapterType);
        } else if (Array.isArray(nicNetworkAdapterType) && nicNetworkAdapterType.length === 0) {
            setNetworkAdapterList([]);
        }
    }, [nicNetworkAdapterType]);
    useEffect(() => {
        if (Array.isArray(nicIpModes) && nicIpModes.length > 0) {
            setIpModes(nicIpModes);
        } else if (Array.isArray(nicIpModes) && nicIpModes.length === 0) {
            setIpModes([]);
        }
    }, [nicIpModes]);
    return (
        <div>
            <Label className="add-new-nic-title ml-5 mt-5">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.ADD_TITLE}</Label>
            <div className="grid grid-cols-4 m-5">
                <div className="col-span-1 pr-6">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="nic-isPrimary"
                            data-testid="nic-isPrimary"
                            checked={nicFormData.primaryNic}
                            onChange={(e) => handleNicFormChange('primaryNic', e.target.checked)}
                        />
                        <Label className="nic-input-label">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.MARK_PRIMARY}</Label>
                    </div>
                    <div className="mt-4">
                        <Label className="createCourse-label m-0">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.STATE}</Label>
                        <Toggle
                            className="ml-4 pointer-events-none"
                            id="state"
                            data-testid="state"
                            checked={nicFormData.connected}
                            onChange={(e) => handleNicFormChange('connected', e.target.checked)}
                        />
                        <div>
                            <Label className="ml-8 font-14">
                                {nicFormData.connected
                                    ? CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.CONNECTED
                                    : CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.DISCONNECTED}
                            </Label>
                        </div>
                    </div>
                </div>

                {/* Right column for network settings, as a vertical stack */}
                <div className="col-span-3">
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="filterBy">
                            <Label required className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.NETWORK}</Label>
                            {isEditMode && (
                                <Select
                                    height={4}
                                    className={`createCourse-vpp-template !outline-none ${!nicError.network ? '!border-none' : ''}`}
                                    data-testid="nic-network-select"
                                    id="nic-network-select"
                                    onChange={(e) => handleNicFormChange('network', e.target.value)}
                                    placeholder="Select"
                                    size="regular"
                                    value={nicFormData.network}
                                >
                                    {
                                        networkList.map((network: INetworkList, index: number) => (
                                            <SelectOption
                                                key={index}
                                                label={network.networkText}
                                                value={network.networkValue}
                                            />
                                        ))
                                    }

                                </Select>
                            )}
                            {!isEditMode && (
                                <Select
                                    height={4}
                                    className={`createCourse-vpp-template !outline-none ${!nicError.network ? '!border-none' : ''}`}
                                    data-testid="nic-network-select"
                                    id="nic-network-select"
                                    onChange={(e) => handleNicFormChange('network', e.target.value)}
                                    placeholder="Select"
                                    size="regular"
                                    error={!!nicError.network}
                                    value={nicFormData.network}
                                >
                                    <SelectOption
                                        key={1}
                                        label={"None"}
                                        value={"None"}
                                    />
                                    <SelectOption
                                        key={1}
                                        label={"Default"}
                                        value={"default"}
                                    />
                                </Select>
                            )}
                            {nicError.network && (
                                <ControlError className="text-xs" showIcon={false}>{nicError.network}</ControlError>
                            )}
                        </div>
                        <div>
                            <Label required={nicFormData.network !== 'None'} className="createCourse-label mb-2">
                                {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.NW_ADAPTER_TYPE}
                            </Label>
                            <div className="filterBy">
                                {nicFormData.network !== 'None' ? (
                                    <Select
                                        height={4}
                                        id="network-adapter-select"
                                        data-testid="network-adapter-type-select"
                                        className={`createCourse-vpp-template !outline-none ${!nicError.networkAdapterType ? '!border-none' : ''}`}
                                        onChange={(e) => handleNicFormChange('networkAdapterType', e.target.value)}
                                        placeholder="Select"
                                        size="regular"
                                        error={!!nicError.networkAdapterType}
                                    >
                                        {networkAdapterList.map((adapter: INetworkAdapter, index: number) => (
                                            <SelectOption
                                                key={index}
                                                label={adapter.naTypeText}
                                                value={adapter.naTypeValue}
                                            />
                                        ))}
                                    </Select>
                                ) : null}
                                {nicFormData.network === 'None' ? (
                                    <Input
                                        id="network-adapter-input"
                                        className="createCourse-duration-input-field !border-none"
                                        data-testid="network-adapter-input"
                                        size="regular"
                                        disabled
                                        readOnly
                                        value={nicFormData.networkAdapterType}
                                    // defaultValue={nicFormData.networkAdapterType}
                                    />
                                ) : null}
                            </div>
                            {nicError.networkAdapterType && (
                                <ControlError className="text-xs" showIcon={false}>{nicError.networkAdapterType}</ControlError>
                            )}
                        </div>
                        <div>
                            <Label className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.MAC_ADDRESS}</Label>
                            <Input
                                id="nic-mac-address"
                                className="createCourse-duration-input-field !border-none"
                                data-testid="nic-mac-address"
                                size="regular"
                                disabled
                                readOnly
                                placeholder="Mac Address"
                                value={nicFormData.macAddress}
                            // defaultValue={nicFormData.macAddress}
                            />
                        </div>
                        <div>
                            <Label required={nicFormData.network !== 'None'} className="createCourse-label mb-2">
                                {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.IP_MODE}
                            </Label>
                            <div className="filterBy">
                                {nicFormData.network !== 'None' && (
                                    <Select
                                        height={4}
                                        id="nic-ip-mode"
                                        data-testid="nic-ip-mode"
                                        className={`createCourse-vpp-template !outline-none ${!nicError.ipMode ? '!border-none' : ''}`}
                                        onChange={(e) => handleNicFormChange('ipMode', e.target.value)}
                                        placeholder="Select"
                                        size="regular"
                                        disabled={nicFormData.network === 'none'}
                                        error={!!nicError.ipMode}
                                        value={nicFormData.ipMode}
                                    >
                                        {ipModes.map((mode: INicIPModes, index: number) => (
                                            <SelectOption
                                                key={index}
                                                label={mode.ipModeText}
                                                value={mode.ipModeValue}
                                            />
                                        ))}
                                    </Select>
                                )}
                                {nicFormData.network === 'None' ? (
                                    <Input
                                        id="nic-ip-mode"
                                        className="createCourse-duration-input-field !border-none"
                                        data-testid="nic-ip-mode"
                                        size="regular"
                                        disabled
                                        readOnly
                                        value={nicFormData.networkAdapterType}
                                    // defaultValue={nicFormData.networkAdapterType}
                                    />
                                ) : null}
                            </div>
                            {nicError.ipMode && (
                                <ControlError className="text-xs" showIcon={false}>{nicError.ipMode}</ControlError>
                            )}
                        </div>

                        <div>
                            <Label className="createCourse-label mb-2">{CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.IP_TYPE}</Label>
                            <Input
                                id="nic-ip-type"
                                className="createCourse-duration-input-field !border-none"
                                data-testid="nic-ip-type"
                                size="regular"
                                disabled
                                readOnly
                                value={nicFormData.ipType}
                            // defaultValue={nicFormData.ipType}
                            />
                        </div>
                        <div>
                            <Label required={nicFormData.ipMode === 'manual'} className="createCourse-label mb-2">
                                {CREATE_COURSE_CONSTANTS.CREATE_VM_MODAL.NIC_CONFIG.IP_ADDRESS}
                            </Label>
                            {nicFormData.ipMode === 'manual' ? (
                                <Input
                                    id="ip-address-input"
                                    className={`createCourse-duration-input-field ${!nicError.ipAddress ? '!border-none' : ''}`}
                                    data-testid="ip-address-input"
                                    size="regular"
                                    placeholder="IP Address (e.g. 192.168.1.10)"
                                    value={nicFormData.ipAddress}
                                    onChange={(e) => handleNicFormChange('ipAddress', e.target.value)}
                                    error={!!nicError.ipAddress}
                                    onBlur={() => {
                                        if (nicFormData.ipMode === 'manual') {
                                            const ipError = validateIpAddress(nicFormData.ipAddress);
                                            handleNicFormChange('ipAddress', nicFormData.ipAddress);
                                        }
                                    }}
                                />
                            ) : (
                                <Input
                                    id="ip-address-input"
                                    className={`createCourse-duration-input-field ${!nicError.ipAddress ? '!border-none' : ''}`}
                                    data-testid="ip-address-input"
                                    size="regular"
                                    placeholder="IP Address"
                                    value={nicFormData.ipAddress}
                                    // defaultValue={nicFormData.ipAddress}
                                    disabled
                                    readOnly
                                />
                            )}
                            {nicError.ipAddress && nicFormData.ipMode === 'manual' && (
                                <ControlError className="text-xs" showIcon={false}>{nicError.ipAddress}</ControlError>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="display flex justify-end mt-4 pb-4 mr-4">
                <Button
                    className="text-base"
                    id="add-nic-to-table"
                    data-testid="add-nic-to-table"
                    onClick={handleAddNicToTable}
                    size="regular"
                    variant="primary"
                    iconPosition="right"
                >
                    {CREATE_COURSE_CONSTANTS.BUTTONS.ADD}
                </Button>
            </div>
        </div>
    );
};

export default NicConfiguration;